'use client';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@utils/constants';
import getQualifiedImageUrl from '@utils/imageUrlProcessor';
import { cva, cx } from 'cva';
import Image from 'next/image';
import React, { useEffect } from 'react';
import { FaUser } from 'react-icons/fa';

const avatar = cva('avatar', {
  variants: {
    size: {
      xs: ['w-6 h-6 shrink-0 grow-0 text-16 font-normal lg:font-20'],
      sm: ['w-9 h-9 shrink-0 grow-0 text-16 font-normal lg:font-20'],
      md: ['w-12 h-12 shrink-0 grow-0 text-28 font-semibold'],
      mdlg: ['w-[75px] h-[75px] lg:w-[110px] lg:h-[110px] shrink-0 grow-0 text-32 font-semibold'],
      lg: [
        'w-[100px] h-[100px] lg:w-[151px] lg:h-[151px] shrink-0 grow-0 text-60 font-semibold',
      ],
      xl: ['w-40 h-40 shrink-0 grow-0 text-28 font-semibold'],
      '2xl': ['w-60 h-60 shrink-0 grow-0 text-28 font-semibold'],
    },
    profile: {
      user: [],
      company: [],
      npo: [],
    },
    shape: {
      circle: ['rounded-full'],
      square: ['rounded-md'],
    },
  },
  compoundVariants: [
    {
      size: 'lg',
      className: 'border-4 lg:border-[5px] border-white',
    },
  ],
  defaultVariants: {
    size: 'md',
    profile: 'user',
    shape: 'circle',
  },
});

const avatarImage = cva('avatar-image', {
  variants: {
    fit: {
      contain: ['object-contain'],
      cover: ['object-cover'],
    },
    shape: {
      circle: ['rounded-full'],
      square: ['rounded-md'],
    },
  },
  defaultVariants: { fit: 'contain', shape: 'circle' },
});

function Avatar(props) {
  const {
    src,
    alt,
    className,
    size,
    shape,
    fit,
    profile = 'user',
    ...rest
  } = props;

  const imageUrl = src ? getQualifiedImageUrl(src) : '';
  const isNekiHostedImage = imageUrl.startsWith(API_BASE_URL);
  const [hasImage, setHasImage] = React.useState(imageUrl.length > 0);
  const avatarCharacter = alt ? alt[0] : '';

  useEffect(() => {
    setHasImage(imageUrl.length > 0);
  }, [imageUrl]);

  const avatarProps = avatar({
    size: size,
    shape: shape,
  });

  const avatarImageProps = avatarImage({
    fit: fit,
    shape: shape,
  });

  const handleImageError = (e) => {
    console.log(`Error loading image: ${imageUrl}`);
    setHasImage(false);
  };

  if (process.env.NODE_ENV === 'development' && hasImage && !alt) {
    console.warn(
      `Avatar component is missing alt prop. Please add alt prop to the Avatar component: ${imageUrl}`,
    );
  }
  return (
    <div
      className={cn(
        avatarProps,
        'relative flex items-center justify-center bg-body-background',
        !hasImage && profile === 'user' && 'p-2',
        shape === 'square' && hasImage && 'w-full lg:w-full',
        className,
      )}
      {...rest}>
      {hasImage && isNekiHostedImage && (
        <Image
          src={imageUrl || ''}
          alt={alt}
          className={avatarImageProps}
          {...rest}
          fill
          onError={handleImageError}
        />
      )}
      {hasImage && !isNekiHostedImage && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={imageUrl}
          alt={alt}
          className={cn(
            avatarImageProps,
            'absolute h-full w-full text-center text-20',
          )}
          {...rest}
          onError={handleImageError}
        />
      )}
      {!hasImage && profile === 'user' && (
        <FaUser className="h-full w-full text-text-disabled" />
      )}
      {!hasImage && profile === 'npo' && (
        <div className="flex h-full w-full items-center justify-center rounded-full border-2 border-white bg-secondary-light-1 text-center">
          <p className="text-chips-secondary">{avatarCharacter}</p>
        </div>
      )}
      {!hasImage && profile === 'company' && (
        <div className="flex h-full w-full items-center justify-center rounded-full border-2 border-white bg-primary-light-1 text-center">
          <p className="text-chips-primary">{avatarCharacter}</p>
        </div>
      )}
    </div>
  );
}

export default Avatar;
